import * as React from 'react';
import { useDispatch } from 'react-redux';
import { useAtomValue } from 'jotai';
import { DimensionTableContainer, DimensionTablesWrapper } from './styled';
import DimensionTableWidget from './dimension-table';
import DimensionHeader from './header';
import useCalcTableWidth from './hooks/useCalcTableWidth';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable';
import { setDimensions } from '@store/trends/base/actions';
import { tid } from '@utils/test';
import ErrorDimensionWidget from './dimension-table/StaticDimensionTables/ErrorDimensionWidget';
import MappedEventWidget from './dimension-table/StaticDimensionTables/MappedEventWidget';
import AppErrorWidget from './dimension-table/StaticDimensionTables/AppErrorWidget';
import FlowErrorAnalysisWidget from './dimension-table/StaticDimensionTables/FlowErrorAnalysisWidget';
import ANRReasonWidget from './dimension-table/StaticDimensionTables/ANRReasonWidget';
import RageClickWidget from './dimension-table/StaticDimensionTables/RageClickWidget';
import Nw5xxToBackgroundDimensionWidget from './dimension-table/StaticDimensionTables/Nw5XXtoBackgroundWidget';
import ClickToWebErrorWidget from './dimension-table/StaticDimensionTables/ClickToWebErrorWidget';
import PageLoopWidget from './dimension-table/StaticDimensionTables/PageLoopWidget';
import ExcessiveAppRestartWidget from './dimension-table/StaticDimensionTables/ExcessiveAppRestartWidget';
import AppCrashV2Widget from './dimension-table/StaticDimensionTables/AppCrashV2Widget';
import { useDimensionOperations } from './hooks/useDimensionOperations';
import useTableMetrics from './hooks/useTableMetrics';
import { horizontalLayoutAtom } from '../metrics-panel-hz/atoms';

export interface DimensionTablesProps {
  hiddenDimensions?: string[];
  disableMetricTags?: boolean;
  disableOrderBySelector?: boolean;
}

function DimensionTables(props: DimensionTablesProps) {
  const {
    hiddenDimensions = [],
    disableMetricTags = false,
    disableOrderBySelector = false,
  } = props;
  const { selectedDimensions: dimensions } = useDimensionOperations();

  const horizontalLayout = useAtomValue(horizontalLayoutAtom);

  useTableMetrics();

  const sensors = useSensors(useSensor(PointerSensor));

  const dispatch = useDispatch();

  function handleDragEnd(event) {
    const { active, over } = event;
    const sourceIdx = dimensions.indexOf(active.id);
    const destIdx = dimensions.indexOf(over.id);

    // create new dimension list
    const newDimensionList = Array.from(dimensions);
    const [reorderedItem] = newDimensionList.splice(sourceIdx, 1);
    newDimensionList.splice(destIdx, 0, reorderedItem);

    dispatch(setDimensions(newDimensionList));
  }

  const { ref, width }: any = useCalcTableWidth();

  return (
    <DimensionTablesWrapper {...tid('dimension-panel')}>
      <DimensionHeader
        disableMetricTags={disableMetricTags}
        disableOrderBySelector={disableOrderBySelector}
      />
      <DimensionTableContainer
        ref={ref}
        id="dimension-table"
        width={width}
        horizontalLayout={horizontalLayout}
      >
        <ErrorDimensionWidget />
        <MappedEventWidget />
        <AppErrorWidget />
        <FlowErrorAnalysisWidget />
        <ANRReasonWidget />
        <RageClickWidget />
        <Nw5xxToBackgroundDimensionWidget />
        <ClickToWebErrorWidget />
        <PageLoopWidget />
        <ExcessiveAppRestartWidget />
        <AppCrashV2Widget />
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={dimensions} strategy={rectSortingStrategy}>
            {dimensions.map((dimension) => (
              <DimensionTableWidget
                key={dimension}
                dimension={dimension}
                hiddenDimensions={hiddenDimensions}
              />
            ))}
          </SortableContext>
        </DndContext>
      </DimensionTableContainer>
    </DimensionTablesWrapper>
  );
}

export default DimensionTables;
